import { RRule } from './rrule.js';
import {
  addDays,
  formatShortDate,
  weekdayOf,
  type LocalDate,
  type LocalTime,
  type Weekday,
} from './time.js';

/**
 * Repetition as the UI understands it. Persisted as an RFC 5545 RRULE string (without DTSTART);
 * the anchor date (DTSTART) is the item's `startDate`, the end is its `endDate`.
 */
export type RepeatSpec =
  | { type: 'once' }
  | { type: 'daily'; interval: number }
  | { type: 'weekly'; interval: number; days: Weekday[] }
  | { type: 'monthly-day'; interval: number; day: number }
  | { type: 'monthly-weekday'; interval: number; weekday: Weekday; nth: 1 | 2 | 3 | 4 | -1 }
  | { type: 'custom'; rrule: string };

export interface RecurrenceRule {
  rrule: string | null;
  startDate: LocalDate;
  endDate: LocalDate | null;
}

const BYDAY_CODES = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as const;
const ALLOWED_FREQ = new Set(['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY']);
export const MAX_INTERVAL = 365;

/**
 * Strict whitelist of RRULE parts. Everything else (COUNT, UNTIL, BYSETPOS, BYHOUR, …) is rejected:
 * the series end is always `endDate`, and exotic parts can make rrule search for years.
 */
const PART_PATTERNS: Record<string, RegExp> = {
  FREQ: /^(DAILY|WEEKLY|MONTHLY|YEARLY)$/,
  INTERVAL: /^[1-9]\d{0,2}$/,
  BYDAY: /^([+-]?[1-5])?(MO|TU|WE|TH|FR|SA|SU)(,([+-]?[1-5])?(MO|TU|WE|TH|FR|SA|SU))*$/,
  BYMONTHDAY: /^-?([1-9]|[12]\d|3[01])(,-?([1-9]|[12]\d|3[01]))*$/,
  BYMONTH: /^([1-9]|1[0-2])(,([1-9]|1[0-2]))*$/,
  WKST: /^(MO|TU|WE|TH|FR|SA|SU)$/,
};
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const dayCode = (d: Weekday): string => BYDAY_CODES[d - 1] ?? 'MO';
const codeToDay = (code: string): Weekday | null => {
  const i = (BYDAY_CODES as readonly string[]).indexOf(code);
  return i === -1 ? null : ((i + 1) as Weekday);
};

function withInterval(parts: string[], interval: number): string {
  if (interval > 1) parts.splice(1, 0, `INTERVAL=${interval}`);
  return parts.join(';');
}

export function specToRRule(spec: RepeatSpec): string | null {
  switch (spec.type) {
    case 'once':
      return null;
    case 'daily':
      return withInterval(['FREQ=DAILY'], spec.interval);
    case 'weekly': {
      const days = [...new Set(spec.days)].sort((a, b) => a - b).map(dayCode);
      const parts = ['FREQ=WEEKLY'];
      if (days.length > 0) parts.push(`BYDAY=${days.join(',')}`);
      return withInterval(parts, spec.interval);
    }
    case 'monthly-day':
      return withInterval(['FREQ=MONTHLY', `BYMONTHDAY=${spec.day}`], spec.interval);
    case 'monthly-weekday':
      return withInterval(
        ['FREQ=MONTHLY', `BYDAY=${spec.nth}${dayCode(spec.weekday)}`],
        spec.interval,
      );
    case 'custom':
      return spec.rrule;
  }
}

function parseParts(rrule: string): Map<string, string> {
  const map = new Map<string, string>();
  for (const part of rrule.replace(/^RRULE:/i, '').split(';')) {
    const [k, v] = part.split('=');
    if (k && v !== undefined) map.set(k.toUpperCase(), v.toUpperCase());
  }
  return map;
}

/** Best-effort mapping of a stored RRULE back to the UI presets; anything else is `custom`. */
export function rruleToSpec(rrule: string | null): RepeatSpec {
  if (!rrule) return { type: 'once' };
  const p = parseParts(rrule);
  const known = new Set(['FREQ', 'INTERVAL', 'BYDAY', 'BYMONTHDAY']);
  if ([...p.keys()].some((k) => !known.has(k))) return { type: 'custom', rrule };
  const interval = Number(p.get('INTERVAL') ?? '1');
  const freq = p.get('FREQ');
  const byday = p.get('BYDAY');
  const bymonthday = p.get('BYMONTHDAY');

  if (freq === 'DAILY' && !byday && !bymonthday) return { type: 'daily', interval };
  if (freq === 'WEEKLY' && !bymonthday) {
    const days = (byday ? byday.split(',') : []).map(codeToDay);
    if (days.every((d): d is Weekday => d !== null)) return { type: 'weekly', interval, days };
  }
  if (freq === 'MONTHLY' && bymonthday && !byday && !bymonthday.includes(',')) {
    return { type: 'monthly-day', interval, day: Number(bymonthday) };
  }
  if (freq === 'MONTHLY' && byday && !bymonthday) {
    const m = /^(-1|[1-4])(MO|TU|WE|TH|FR|SA|SU)$/.exec(byday);
    const weekday = m?.[2] ? codeToDay(m[2]) : null;
    if (m && weekday) {
      return {
        type: 'monthly-weekday',
        interval,
        weekday,
        nth: Number(m[1]) as 1 | 2 | 3 | 4 | -1,
      };
    }
  }
  return { type: 'custom', rrule };
}

/** Returns an error message (Slovene, user-facing) or null when the RRULE is acceptable. */
export function validateRRule(rrule: string): string | null {
  if (rrule.length > 255) return 'Pravilo je predolgo.';
  const body = rrule.trim().replace(/^RRULE:/i, '');
  if (!/^[A-Za-z0-9=;,+-]+$/.test(body)) return 'Pravilo vsebuje nedovoljene znake.';

  const parts = new Map<string, string>();
  for (const part of body.split(';')) {
    const [key, value, extra] = part.toUpperCase().split('=');
    if (!key || value === undefined || extra !== undefined) return 'Pravila ni mogoče prebrati.';
    if (parts.has(key)) return `Del pravila ${key} se ponovi.`;
    const pattern = PART_PATTERNS[key];
    if (!pattern) return `Del pravila ${key} ni dovoljen.`;
    if (!pattern.test(value)) return `Neveljavna vrednost za ${key}.`;
    parts.set(key, value);
  }
  const freq = parts.get('FREQ');
  if (!freq || !ALLOWED_FREQ.has(freq)) {
    return 'Dovoljena so dnevna, tedenska, mesečna in letna pravila.';
  }
  const interval = Number(parts.get('INTERVAL') ?? '1');
  if (interval > MAX_INTERVAL) return `Interval mora biti med 1 in ${MAX_INTERVAL}.`;

  // Month + day-of-month combinations that can never happen (e.g. 30. 2.) would make rrule
  // search until the year 9999.
  const months = parts.get('BYMONTH')?.split(',').map(Number);
  const monthDays = parts.get('BYMONTHDAY')?.split(',').map(Number);
  if (months && monthDays) {
    const possible = months.some((m) =>
      monthDays.some((d) => Math.abs(d) <= (DAYS_IN_MONTH[m - 1] ?? 0)),
    );
    if (!possible) return 'Ta datum v izbranih mesecih ne obstaja.';
  }
  return null;
}

const utcMidnight = (date: LocalDate): Date => new Date(`${date}T00:00:00Z`);
const toLocalDate = (d: Date): LocalDate => d.toISOString().slice(0, 10);

/**
 * All dates in `[from, to]` (inclusive) on which the rule occurs.
 * Recurrence is computed on "floating" calendar dates (UTC midnight), so daylight-saving
 * transitions can never shift an occurrence to another day; the wall-clock time is applied later.
 */
export function occurrenceDates(rule: RecurrenceRule, from: LocalDate, to: LocalDate): LocalDate[] {
  const lo = rule.startDate > from ? rule.startDate : from;
  const hi = rule.endDate && rule.endDate < to ? rule.endDate : to;
  if (lo > hi) return [];
  if (!rule.rrule) return rule.startDate >= lo && rule.startDate <= hi ? [rule.startDate] : [];

  const options = RRule.parseString(rule.rrule.replace(/^RRULE:/i, ''));
  // Weekly rule without BYDAY repeats on the anchor's weekday (RFC 5545 semantics, made explicit).
  if (options.freq === RRule.WEEKLY && options.byweekday == null) {
    options.byweekday = [weekdayOf(rule.startDate) - 1];
  }
  const r = new RRule({
    ...options,
    dtstart: utcMidnight(rule.startDate),
    until: rule.endDate ? utcMidnight(rule.endDate) : null,
  });
  return r.between(utcMidnight(lo), utcMidnight(hi), true).map(toLocalDate);
}

export function nextOccurrences(rule: RecurrenceRule, from: LocalDate, count: number): LocalDate[] {
  return occurrenceDates(rule, from, addDays(from, 366 * 2)).slice(0, count);
}

// ---------------------------------------------------------------------------
// Plain-language summary (Slovene)

const DAY_SHORT = ['pon', 'tor', 'sre', 'čet', 'pet', 'sob', 'ned'] as const;
const DAY_NAME = ['ponedeljek', 'torek', 'sreda', 'četrtek', 'petek', 'sobota', 'nedelja'] as const;
const FEMININE_DAYS = new Set<Weekday>([3, 6, 7]);
const ORDINAL_M = ['', 'drugi', 'tretji', 'četrti', 'peti', 'šesti'] as const;

function joinSl(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} in ${items[items.length - 1]}`;
}

function everyNth(interval: number, unit: string): string {
  if (interval === 1) return `Vsak ${unit}`;
  const word = ORDINAL_M[interval - 1];
  return word ? `Vsak ${word} ${unit}` : `Vsak ${interval}. ${unit}`;
}

const NTH_WORDS = new Map<number, [masculine: string, feminine: string]>([
  [1, ['Prvi', 'Prva']],
  [2, ['Drugi', 'Druga']],
  [3, ['Tretji', 'Tretja']],
  [4, ['Četrti', 'Četrta']],
  [-1, ['Zadnji', 'Zadnja']],
]);

function nthWeekday(nth: number, weekday: Weekday): string {
  const words = NTH_WORDS.get(nth);
  const word = words ? words[FEMININE_DAYS.has(weekday) ? 1 : 0] : `${nth}.`;
  return `${word} ${DAY_NAME[weekday - 1] ?? ''}`;
}

export function describeRepeat(spec: RepeatSpec, startDate: LocalDate): string {
  switch (spec.type) {
    case 'once':
      return `Enkrat, ${formatShortDate(startDate)}`;
    case 'daily':
      return everyNth(spec.interval, 'dan');
    case 'weekly': {
      const days = spec.days.length > 0 ? [...spec.days].sort() : [weekdayOf(startDate)];
      const key = days.join(',');
      let text: string;
      if (key === '1,2,3,4,5,6,7') text = 'Vsak dan';
      else if (key === '1,2,3,4,5') text = 'Vsak delavnik';
      else if (key === '6,7') text = 'Ob vikendih';
      else text = `Vsak ${joinSl(days.map((d) => DAY_SHORT[d - 1] ?? ''))}`;
      if (spec.interval === 1) return text;
      return `${everyNth(spec.interval, 'teden')}: ${text.replace(/^Vsak /, '').toLowerCase()}`;
    }
    case 'monthly-day': {
      const base = spec.day === -1 ? 'Zadnji dan v mesecu' : `Vsak ${spec.day}. v mesecu`;
      return spec.interval === 1 ? base : `${base}, vsakih ${spec.interval} mesecev`;
    }
    case 'monthly-weekday': {
      const base = `${nthWeekday(spec.nth, spec.weekday)} v mesecu`;
      return spec.interval === 1 ? base : `${base}, vsakih ${spec.interval} mesecev`;
    }
    case 'custom':
      return 'Ponavljanje po meri';
  }
}

export function formatTimeSl(time: LocalTime): string {
  return time.replace(/^0(\d)/, '$1');
}

/** "Vsak pon, sre in pet ob 6:00, do 31. 1. 2027" */
export function describeSchedule(input: {
  rrule: string | null;
  startDate: LocalDate;
  endDate: LocalDate | null;
  startTime: LocalTime;
  today?: LocalDate;
}): string {
  const spec = rruleToSpec(input.rrule);
  let text = `${describeRepeat(spec, input.startDate)} ob ${formatTimeSl(input.startTime)}`;
  if (spec.type !== 'once') {
    if (input.today && input.startDate > input.today)
      text += `, od ${formatShortDate(input.startDate)}`;
    if (input.endDate) text += `, do ${formatShortDate(input.endDate)}`;
  }
  return text;
}
